<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useToast } from 'vue-toast-notification'
import { useTranslate } from '@tolgee/vue'

import { useEditorStore } from '@/stores/Editor'
import { getEditorSettings } from '@/api'
import type { IEditorSettings } from '@/api/interfaces'
import { isEmptyObject, transformObjectEmptyValuesToNull, updatedDiff } from '@/helpers/utils'

import EyeIcon from '@/components/icons/EyeIcon.vue'
import CloseIcon from '@/components/icons/CloseIcon.vue'
import ArrowBack from '@/components/icons/ArrowBack.vue'
import ConfirmDialog from '@/components/common/ConfirmDialog.vue'
import GreensparkLogo from '@/components/icons/GreensparkLogo.vue'

const route = useRoute()
const store = useEditorStore()
const toast = useToast()
const { t } = useTranslate('local')
const isConfirmDialogOpen = ref(false)
const initialEditorSettings = ref<Partial<IEditorSettings>>({})
const companyId: string = route.params.id as string

const confirmExit = () => {
  isConfirmDialogOpen.value = false
  window.location.href = import.meta.env.VITE_FRONTEND_URL
}

const getUdpatedEditorSettings = (): Partial<IEditorSettings> => {
  return updatedDiff(
    JSON.parse(JSON.stringify(initialEditorSettings.value)),
    JSON.parse(JSON.stringify(store.editor)),
  )
}

const navigateBack = async () => {
  if (!isEmptyObject(getUdpatedEditorSettings())) {
    isConfirmDialogOpen.value = true
  } else {
    confirmExit()
  }
}

const setInitialEditorSettings = async () => {
  const { data } = await getEditorSettings(companyId)
  initialEditorSettings.value = {
    ...data.publicPageEditorSettings,
  }
}

const hasEditorChanges = computed(() => {
  return !isEmptyObject(getUdpatedEditorSettings())
})

const togglePreviewMode = () => {
  store.previewMode = !store.previewMode
  store.activeDevice = 'desktop'
}

const saveAndPublish = async () => {
  try {
    const updatedData = getUdpatedEditorSettings()

    if (isEmptyObject(updatedData)) {
      return
    }

    store.publishLoading = true

    await store.saveEditorSettings(transformObjectEmptyValuesToNull(updatedData))

    setInitialEditorSettings()

    toast.success(t.value('EditorHeader.buttons.saveAndPublish.success'))
  } catch (e) {
    toast.error(t.value('EditorHeader.buttons.saveAndPublish.error'))
  } finally {
    store.publishLoading = false
  }
}

onMounted(async () => {
  setInitialEditorSettings()
})
</script>

<template>
  <header class="editor-header">
    <div class="editor-header-wrapper">
      <div class="editor-header-logo">
        <a
          v-if="!store.previewMode"
          class="d-none d-md-block"
          href="https://www.getgreenspark.com"
          target="_blank"
        >
          <GreensparkLogo height="32" width="160" />
        </a>
        <GsButton
          v-if="!store.previewMode"
          @click="navigateBack"
          type="outlined"
          class="editor-back-button d-md-none"
        >
          <ArrowBack class="icon" />
          {{ t('EditorHeader.buttons.back') }}
        </GsButton>
        <GsButton
          v-if="store.previewMode"
          @click="togglePreviewMode"
          class="editor-back-button"
          type="outlined"
        >
          <ArrowBack class="icon" />
          {{ t('EditorHeader.buttons.backToEditor') }}
        </GsButton>
      </div>
      <nav class="editor-header-nav">
        <GsButton v-if="!store.previewMode" @click="togglePreviewMode" type="secondary" width="165">
          <EyeIcon class="icon" />
          {{ t('EditorHeader.buttons.preview') }}
        </GsButton>
        <GsButton
          @click="saveAndPublish"
          type="success"
          width="210"
          :disabled="store.publishLoading || !hasEditorChanges || !store.valid"
          :loading="store.publishLoading"
        >
          {{ t('EditorHeader.buttons.saveAndPublish.label') }}
        </GsButton>
        <button @click="navigateBack" class="editor-close d-none d-md-block">
          <CloseIcon />
        </button>
        <ConfirmDialog
          v-model="isConfirmDialogOpen"
          @confirm="confirmExit"
          :title="t('EditorHeader.unsavedChanges.title')"
          :description="t('EditorHeader.unsavedChanges.description')"
          :confirmText="t('EditorHeader.unsavedChanges.ok')"
        />
      </nav>
    </div>
  </header>
</template>

<style lang="scss" scoped>
.editor-header {
  grid-area: header;
  width: 100%;
  height: 56px;
  background: rgba(250, 250, 250, 0.8);
  box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(2px);
  z-index: 9998;

  @include media('md-and-up') {
    height: 70px;
  }
}

.editor-header-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 56px;
  padding: 8px 4px;

  @include media('md-and-up') {
    height: 70px;
    padding: 0 20px;
  }
}

.btn {
  .icon {
    margin-right: 6px;
  }

  @include media('sm-and-down') {
    font-size: 16px;
  }

  .v-progress-circular {
    margin: 0 57px;
  }
}

.editor-header-nav {
  display: flex;
  gap: 10px;

  .btn {
    @include media('sm-and-down') {
      padding: 4px 12px;
    }
  }
}

.editor-close,
.editor-back-button {
  padding: 0 6px;
  border: 0 !important;
}
</style>
